import type { AudienceFilterKey, AudienceFilters } from '../types';

/**
 * The purchase windows, in days, a definition can carry.
 *
 * It mirrors `PURCHASE_LOOKBACK_WINDOWS` in proxima-audiences-commons `src/types/audience.types.ts`.
 * proxima-audiences rejects any other `purchaseLookbackDays` with a 400, since the consumer graph precomputes an order count for these windows only.
 */
export const PURCHASE_LOOKBACK_WINDOWS = [90, 180, 365] as const;

export type PurchaseLookbackDays = (typeof PURCHASE_LOOKBACK_WINDOWS)[number];

/** Whether `days` is one of `PURCHASE_LOOKBACK_WINDOWS`. */
export const isPurchaseLookbackWindow = (days: number): days is PurchaseLookbackDays =>
  (PURCHASE_LOOKBACK_WINDOWS as readonly number[]).includes(days);

/**
 * The purchase window a draft with no window rule uses.
 *
 * proxima-audiences fills it in only when `purchaseLookbackDays` is absent: create maps null to the column default, but preview-count validates with a zod `.default()`, which rejects null with a 400.
 * `toRequestFilters` sends it explicitly, so every request carries the window the purchase-window chip shows.
 *
 * It mirrors `DEFAULT_PURCHASE_LOOKBACK_DAYS` in proxima-audiences-commons `src/compiler/audience-definition-params.ts` and the column default on `audience_definition.purchase_lookback_days`.
 * No proxima-audiences endpoint exposes the default, so a change there needs a matching change here.
 */
export const DEFAULT_PURCHASE_LOOKBACK_DAYS: PurchaseLookbackDays = 180;

/* The compiler's pool cap: counts at or above it are "at cap", not exact */
export const MEMBER_COUNT_CAP = 125_000;

/* Seeds below this are flagged as small in the count bar; advisory only */
export const MIN_SEED_SIZE_WARNING = 10_000;

export const EMPTY_FILTERS: AudienceFilters = {
  gender: null,
  ageMin: null,
  ageMax: null,
  incomeMin: null,
  incomeMax: null,
  purchaseLookbackDays: null,
  commerceAttributes: [],
  includedBrandIds: [],
  excludedBrandIds: [],
  includedStates: [],
  excludedStates: [],
  hasDiscountPurchase: null,
  orderCountMin: null,
  orderCountMax: null,
  rankColumn: null,
  uniqueIndividualLimit: null
};

export const FILTER_KEYS = Object.keys(EMPTY_FILTERS) as AudienceFilterKey[];

export type FilterGroupId =
  | 'demographics'
  | 'geography'
  | 'interests'
  | 'brands'
  | 'behaviors'
  | 'size';

export const FILTER_GROUPS: readonly {
  id: FilterGroupId;
  label: string;
  keys: AudienceFilterKey[];
}[] = [
  {
    id: 'demographics',
    label: 'Demographics',
    keys: ['gender', 'ageMin', 'ageMax', 'incomeMin', 'incomeMax']
  },
  { id: 'geography', label: 'Geography', keys: ['includedStates', 'excludedStates'] },
  { id: 'interests', label: 'Interests', keys: ['commerceAttributes'] },
  { id: 'brands', label: 'Brand affinity', keys: ['includedBrandIds', 'excludedBrandIds'] },
  {
    id: 'behaviors',
    label: 'Behaviors',
    keys: ['purchaseLookbackDays', 'orderCountMin', 'orderCountMax', 'hasDiscountPurchase']
  },
  { id: 'size', label: 'Size cap', keys: ['uniqueIndividualLimit'] }
];

/**
 * The filters to show for a saved definition.
 *
 * The service stores the default purchase window on every definition, so a definition whose only filter is that default reads as "no rules", the same as a fresh draft on the new-audience page.
 */
export const displayFilters = (filters: AudienceFilters): AudienceFilters => {
  const others = countActiveFilters(
    filters,
    (Object.keys(EMPTY_FILTERS) as AudienceFilterKey[]).filter(
      key => key !== 'purchaseLookbackDays'
    )
  );
  return others === 0 && filters.purchaseLookbackDays === DEFAULT_PURCHASE_LOOKBACK_DAYS
    ? { ...filters, purchaseLookbackDays: null }
    : filters;
};

/** The filters as sent to preview-count and create: an unset purchase window becomes `DEFAULT_PURCHASE_LOOKBACK_DAYS`. */
export const toRequestFilters = (filters: AudienceFilters): AudienceFilters => ({
  ...filters,
  purchaseLookbackDays: filters.purchaseLookbackDays ?? DEFAULT_PURCHASE_LOOKBACK_DAYS
});

/** Picks the filter fields out of any response that carries them, defaulting arrays to `[]`. */
export const pickFilters = (source: Partial<AudienceFilters>): AudienceFilters => ({
  gender: source.gender ?? null,
  ageMin: source.ageMin ?? null,
  ageMax: source.ageMax ?? null,
  incomeMin: source.incomeMin ?? null,
  incomeMax: source.incomeMax ?? null,
  purchaseLookbackDays: source.purchaseLookbackDays ?? null,
  commerceAttributes: [...(source.commerceAttributes ?? [])],
  includedBrandIds: [...(source.includedBrandIds ?? [])],
  excludedBrandIds: [...(source.excludedBrandIds ?? [])],
  includedStates: [...(source.includedStates ?? [])],
  excludedStates: [...(source.excludedStates ?? [])],
  hasDiscountPurchase: source.hasDiscountPurchase ?? null,
  orderCountMin: source.orderCountMin ?? null,
  orderCountMax: source.orderCountMax ?? null,
  rankColumn: source.rankColumn ?? null,
  uniqueIndividualLimit: source.uniqueIndividualLimit ?? null
});

/**
 * Applies a patch and re-derives dependent fields.
 *
 * `rankColumn` is `ltv` exactly when `uniqueIndividualLimit` is set, matching the service.
 */
export const applyFilterPatch = (
  filters: AudienceFilters,
  patch: Partial<AudienceFilters>
): AudienceFilters => {
  const next = { ...filters, ...patch };
  next.rankColumn = next.uniqueIndividualLimit != null ? 'ltv' : null;
  return next;
};

/* rankColumn rides along with the size cap, so it never counts on its own */
export const countActiveFilters = (filters: AudienceFilters, keys: AudienceFilterKey[]): number =>
  keys.filter(key => key !== 'rankColumn' && isFilterActive(filters, key)).length;

export const isFilterActive = (filters: AudienceFilters, key: AudienceFilterKey): boolean => {
  const value = filters[key];
  if (Array.isArray(value)) return value.length > 0;
  return value != null;
};
